import { useEffect, useMemo } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import type { UseQueryResult } from '@tanstack/react-query';

import {
  useInviteEligibility,
  type InviteEligibilityApplicant,
  type InviteEligibilityResponseData,
} from '@/modules/application-tracker/api';
import { useDebounce } from '@/shared/hooks';
import { SplitFeatureFlag, useSplitFeatureFlag } from '@/shared/providers/feature-flags';
import { useCurrentUser } from '@/stores/useCurrentUser';

import type { InviteLeaseDetailsFormValues } from '../invite-lease-details.form-values';
import {
  buildEligibilityInputs,
  eligibilityQueryParams,
  type EligibilityEmailEntry,
  type EligibilityInputs,
  type InviteEligibilityRowFilter,
} from '../utils/invite-eligibility-inputs.util';
import {
  INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE,
  INVITE_ELIGIBILITY_BLOCK_FIELD_MESSAGE,
} from '../utils/submit-errors.util';
import type { InviteEligibilityPreflightOutcome } from '../utils/invite-eligibility-telemetry.util';

export const INVITE_ELIGIBILITY_DEBOUNCE_MS = 750;
export { INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE } from '../utils/submit-errors.util';
export type {
  InviteEligibilityApplicantRow,
  InviteEligibilityRowFilter,
} from '../utils/invite-eligibility-inputs.util';

const includeEveryEligibilityRow: InviteEligibilityRowFilter = () => true;

const resolveIncludeRow = (
  includeRow: InviteEligibilityRowFilter | undefined,
): InviteEligibilityRowFilter => includeRow ?? includeEveryEligibilityRow;

export const eligibilitySplitTarget = (operatorCompanyId: string | undefined) =>
  operatorCompanyId ? { splitKey: operatorCompanyId } : undefined;

const toInviteEligibilityQueryParams = (inputs: EligibilityInputs, enabled: boolean) => {
  const params = eligibilityQueryParams(inputs);
  return {
    propertyId: params?.property_id,
    addressId: params?.address_id,
    unit: params?.unit,
    emails: inputs.emails,
    inviteAlphaId: params?.invite_alpha_id,
    enabled,
  };
};

type SyncEligibilityErrorsArgs = {
  syncFieldErrors: boolean;
  isFeatureFlagEnabled: boolean;
  isStale: boolean;
  rowCount: number;
  applicantsByFormIndex: Map<number, InviteEligibilityApplicant>;
  setError: ReturnType<typeof useFormContext>['setError'];
  clearErrors: ReturnType<typeof useFormContext>['clearErrors'];
  getFieldState: ReturnType<typeof useFormContext>['getFieldState'];
};

const inputsKey = (inputs: EligibilityInputs): string =>
  JSON.stringify({
    propertyId: inputs.propertyId,
    addressId: inputs.addressId,
    unit: inputs.unit,
    emails: inputs.emails,
    inviteAlphaId: inputs.inviteAlphaId,
  });

const emailFieldPath = (formIndex: number) => `applicants.${formIndex}.email`;

const mapApplicantsByFormIndex = (
  responseApplicants: InviteEligibilityApplicant[] | undefined,
  emailEntries: EligibilityEmailEntry[],
): Map<number, InviteEligibilityApplicant> => {
  const map = new Map<number, InviteEligibilityApplicant>();
  if (!responseApplicants) {
    return map;
  }

  for (const applicant of responseApplicants) {
    const entry = emailEntries[applicant.index];
    if (entry) {
      map.set(entry.formIndex, applicant);
    }
  }
  return map;
};

const countBlockOutcomes = (
  applicantsByFormIndex: Map<number, InviteEligibilityApplicant>,
): number => {
  let count = 0;
  for (const applicant of applicantsByFormIndex.values()) {
    if (applicant.outcome === 'block') {
      count += 1;
    }
  }
  return count;
};

const canRunEligibilityQuery = (
  isFeatureFlagEnabled: boolean,
  inputs: EligibilityInputs,
): boolean =>
  isFeatureFlagEnabled &&
  Boolean(inputs.propertyId) &&
  Boolean(inputs.unit) &&
  inputs.emails.length > 0;

/**
 * Keep the last successful result while a background refetch is in flight.
 * Only treat as stale when inputs moved, the query is off, or we have no success yet / errored.
 */
const hasFreshEligibilityResult = ({
  canQuery,
  inputsMatch,
  isSuccess,
  isError,
}: {
  canQuery: boolean;
  inputsMatch: boolean;
  isSuccess: boolean;
  isError: boolean;
}): boolean => canQuery && inputsMatch && isSuccess && !isError;

const shownEligibilityOutcome = ({
  canQuery,
  inputsMatch,
  isError,
  freshOutcome,
}: {
  canQuery: boolean;
  inputsMatch: boolean;
  isError: boolean;
  freshOutcome: InviteEligibilityResponseData['outcome'] | undefined;
}): InviteEligibilityPreflightOutcome => {
  if (!canQuery) {
    return 'not_checked';
  }
  if (!inputsMatch) {
    return 'pending';
  }
  if (isError) {
    return 'unavailable';
  }
  return freshOutcome ?? 'pending';
};

/** Block and warning both get a red email field; banner carries the copy. */
const shouldHighlightEligibilityEmail = (
  outcome: InviteEligibilityApplicant['outcome'] | undefined,
): boolean => outcome === 'block' || outcome === 'warning';

const syncEligibilityFieldErrors = ({
  syncFieldErrors,
  isFeatureFlagEnabled,
  isStale,
  rowCount,
  applicantsByFormIndex,
  setError,
  clearErrors,
  getFieldState,
}: SyncEligibilityErrorsArgs): void => {
  if (!syncFieldErrors) {
    return;
  }

  const clearEligibilityError = (formIndex: number) => {
    const path = emailFieldPath(formIndex);
    if (getFieldState(path).error?.type === INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE) {
      clearErrors(path);
    }
  };

  const applyEligibilityHighlight = (formIndex: number) => {
    const path = emailFieldPath(formIndex);
    const existingType = getFieldState(path).error?.type;
    // Leave required, format, and duplicate-email errors in place.
    if (existingType && existingType !== INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE) {
      return;
    }
    // Idempotent: Yup `mode: 'all'` wipes manual setError; re-apply without looping.
    if (existingType !== INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE) {
      setError(path, {
        type: INVITE_ELIGIBILITY_BLOCK_ERROR_TYPE,
        message: INVITE_ELIGIBILITY_BLOCK_FIELD_MESSAGE,
      });
    }
  };

  if (!isFeatureFlagEnabled || isStale) {
    for (let formIndex = 0; formIndex < rowCount; formIndex += 1) {
      clearEligibilityError(formIndex);
    }
    return;
  }

  for (let formIndex = 0; formIndex < rowCount; formIndex += 1) {
    const applicant = applicantsByFormIndex.get(formIndex);
    if (shouldHighlightEligibilityEmail(applicant?.outcome)) {
      applyEligibilityHighlight(formIndex);
    } else {
      clearEligibilityError(formIndex);
    }
  }
};

export type UseInviteEligibilityCheckResult = {
  isFeatureEnabled: boolean;
  /** True when banners/errors should not reflect cached data (inputs moved, no success, or error). */
  isStale: boolean;
  hasBlock: boolean;
  /** Renters currently shown a Block banner. */
  blockCount: number;
  getApplicant: (formIndex: number) => InviteEligibilityApplicant | undefined;
  /** What the form shows for the current inputs; telemetry sends it with Submit. */
  shownOutcome: InviteEligibilityPreflightOutcome;
  /** Raw query lifecycle for telemetry; UI should read `isStale` and `getApplicant`. */
  preflight: InviteEligibilityPreflightState;
};

export type InviteEligibilityPreflightState = {
  fetchStatus: UseQueryResult['fetchStatus'];
  status: UseQueryResult['status'];
  data: InviteEligibilityResponseData | undefined;
  error: unknown;
  renterCount: number;
};

export type UseInviteEligibilityCheckOptions = {
  syncFieldErrors?: boolean;
  /**
   * Defaults to every client-valid email (create invite).
   * Pass a stable function. An inline function is a new input on every render and resets the debounce.
   */
  includeRow?: InviteEligibilityRowFilter;
  /** Edit invite passes the invite id so the invite does not match its own rows. */
  inviteAlphaId?: string;
};

/**
 * Debounced invite eligibility preflight for the shared invite form.
 * Call from components under `<LeaseDetailsForm>`'s FormProvider.
 */
export const useInviteEligibilityCheck = (
  options: UseInviteEligibilityCheckOptions = {},
): UseInviteEligibilityCheckResult => {
  const { syncFieldErrors = false, inviteAlphaId } = options;
  const includeRow = resolveIncludeRow(options.includeRow);
  const { user } = useCurrentUser();
  // Split targets this flag by operator company (traffic key), not the anonymous browser id.
  const operatorCompanyId = user?.company?.operator_company_id;
  const { isFeatureFlagEnabled } = useSplitFeatureFlag(
    SplitFeatureFlag.LLX_5050_INVITE_ELIGIBILITY,
    eligibilitySplitTarget(operatorCompanyId),
  );
  const { setError, clearErrors, getFieldState } = useFormContext();
  // Subscribe so we re-apply block field errors after Yup revalidation clears them.
  const { errors, isValidating } = useFormState();

  const address = useWatch({ name: 'address' }) as InviteLeaseDetailsFormValues['address'];
  const unit = useWatch({ name: 'unit' }) as InviteLeaseDetailsFormValues['unit'];
  const applicants = useWatch({ name: 'applicants' }) as InviteLeaseDetailsFormValues['applicants'];

  const liveInputs = useMemo(
    () => buildEligibilityInputs(address, unit, applicants, includeRow, inviteAlphaId),
    [address, unit, applicants, includeRow, inviteAlphaId],
  );
  const debouncedInputs = useDebounce(liveInputs, INVITE_ELIGIBILITY_DEBOUNCE_MS);
  const canQuery = canRunEligibilityQuery(isFeatureFlagEnabled, debouncedInputs);

  const query = useInviteEligibility(toInviteEligibilityQueryParams(debouncedInputs, canQuery));

  const inputsMatch = inputsKey(liveInputs) === inputsKey(debouncedInputs);
  // Disabled queries can keep prior data; require success for the current inputs (refetch ok).
  const hasFreshResult = hasFreshEligibilityResult({
    canQuery,
    inputsMatch,
    isSuccess: query.isSuccess,
    isError: query.isError,
  });
  const isStale = !hasFreshResult;
  const shownOutcome = shownEligibilityOutcome({
    canQuery,
    inputsMatch,
    isError: query.isError,
    freshOutcome: hasFreshResult ? query.data?.outcome : undefined,
  });

  const applicantsByFormIndex = useMemo(() => {
    if (!hasFreshResult) {
      return new Map<number, InviteEligibilityApplicant>();
    }
    return mapApplicantsByFormIndex(query.data?.applicants, debouncedInputs.emailEntries);
  }, [hasFreshResult, query.data, debouncedInputs.emailEntries]);

  const blockCount = countBlockOutcomes(applicantsByFormIndex);

  useEffect(() => {
    if (isValidating) {
      return;
    }
    syncEligibilityFieldErrors({
      syncFieldErrors,
      isFeatureFlagEnabled,
      isStale,
      rowCount: applicants?.length ?? 0,
      applicantsByFormIndex,
      setError,
      clearErrors,
      getFieldState,
    });
  }, [
    syncFieldErrors,
    isFeatureFlagEnabled,
    isStale,
    applicants?.length,
    applicantsByFormIndex,
    setError,
    clearErrors,
    getFieldState,
    errors,
    isValidating,
  ]);

  return {
    isFeatureEnabled: isFeatureFlagEnabled,
    isStale,
    hasBlock: blockCount > 0,
    blockCount,
    getApplicant: (formIndex: number) => applicantsByFormIndex.get(formIndex),
    shownOutcome,
    preflight: {
      fetchStatus: query.fetchStatus,
      status: query.status,
      data: query.data,
      error: query.error,
      renterCount: debouncedInputs.emails.length,
    },
  };
};
